import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProducts } from "@/features/products/products-api";
import { useRegionConfigs } from "@/features/products/product-region-config-api";
import useRegions from "@/features/regions/hooks/use-regions";

// Fixed in-app destinations, in display order. Product deep-links are inserted
// between these two groups when a region is selected.
const FIXED_TOP = [
  { value: "/appointments", label: "/appointments" },
  { value: "/dashboard", label: "/dashboard" },
];
const FIXED_BOTTOM = [
  { value: "/profile/account", label: "/profile/account" },
  { value: "/profile/payment-history", label: "/profile/payment-history" },
];

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Region code from the audience filters; scopes the product deep-links. */
  regionCode?: string;
}

export function DeepLinkSelect({ value, onChange, regionCode }: Props) {
  const { data: products } = useProducts();
  const { data: regionsData } = useRegions(0);
  const regionId = regionCode
    ? regionsData?.regions?.find((r) => r.code === regionCode)?.id
    : undefined;
  const { data: configs } = useRegionConfigs(regionId);

  // Products enabled (ACTIVE region config) and visible in the selected region.
  const productOptions = useMemo(() => {
    if (!regionId || !configs || !products) return [];
    const visibleIds = new Set(
      configs
        .filter(
          (c) =>
            c.status === "ACTIVE" &&
            (c.metadata as Record<string, any>)?.visible !== false,
        )
        .map((c) => c.product_id),
    );
    return products
      .filter((p) => visibleIds.has(p.id))
      .map((p) => ({ value: `/product/${p.id}`, label: `Product · ${p.name}` }));
  }, [regionId, configs, products]);

  const options = [...FIXED_TOP, ...productOptions, ...FIXED_BOTTOM];
  // Keep the current value selectable even if it's no longer in the list
  // (e.g. the region changed after a product path was picked).
  const hasValue = options.some((o) => o.value === value);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder="Select a destination" />
      </SelectTrigger>
      <SelectContent>
        {!hasValue && value ? (
          <SelectItem value={value}>{value}</SelectItem>
        ) : null}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
